import { Component, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ApiService } from 'src/app/_services/api.service';
import { LanguageService } from 'src/app/_services/language.service';

@Component({
  selector: 'app-translations',
  standalone: false,
  templateUrl: './translations.component.html',
  styleUrl: './translations.component.scss',
  providers: [ConfirmationService, MessageService],
})
export class TranslationsComponent implements OnInit {
  rows: any[] = [];
  loading = false;

  tableOptions: any[] = [];
  filterTable: string | null = null;
  replaceLang = 'en-UK';
  search = '';
  onlyMissing = false;

  showPopulateDialog = false;
  populateTable = '';
  populateField = 'des_comp';

  showImportDialog = false;
  importFileName = '';
  importPreviewCount = 0;
  importRows: any[] = [];

  replaceFind = '';
  replaceWith = '';

  languages = [
    { label: 'EN', value: 'en-UK', column: 'des_en' },
    { label: 'FR', value: 'fr-FR', column: 'des_fr' },
    { label: 'DE', value: 'de-DE', column: 'des_de' },
    { label: 'ES', value: 'es-ES', column: 'des_es' },
    { label: 'NL', value: 'nl-NL', column: 'des_nl' },
    { label: 'PT', value: 'pt-PT', column: 'des_pt' },
  ];

  langColumns = ['des_en', 'des_fr', 'des_de', 'des_es', 'des_nl', 'des_pt'];

  constructor(
    private apiService: ApiService,
    public lang: LanguageService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    const params: any = {};
    if (this.filterTable) {
      params.src_table = this.filterTable;
    }
    if (this.search) {
      params.search = this.search;
    }
    if (this.onlyMissing) {
      params.only_missing = 1;
    }

    this.apiService.post('app/des_comp_i18n_listall', params).subscribe({
      next: (data) => {
        this.rows = data.Result || [];
        const tables = data.Tables || [];
        this.tableOptions = (tables as string[]).map((t) => ({ label: t, value: t }));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.lang.el('Errore'),
          detail: this.lang.el('Caricamento fallito'),
        });
      },
    });
  }

  populate(): void {
    if (!this.populateTable || !this.populateField) {
      this.messageService.add({
        severity: 'warn',
        summary: this.lang.el('Attenzione'),
        detail: this.lang.el('Tabella e campo obbligatori'),
      });
      return;
    }

    this.loading = true;
    this.apiService
      .post('app/des_comp_i18n_populate', {
        src_table: this.populateTable.trim(),
        field: this.populateField.trim(),
      })
      .subscribe({
        next: (data) => {
          this.loading = false;
          if (data.Result === 'error') {
            this.messageService.add({
              severity: 'error',
              summary: this.lang.el('Errore'),
              detail: data.Message || this.lang.el('Populate fallito'),
            });
            return;
          }
          const r = data.Result;
          this.messageService.add({
            severity: 'success',
            summary: this.lang.el('Populate'),
            detail:
              this.lang.el('Inseriti') +
              ': ' +
              r.inserted +
              ' — ' +
              this.lang.el('Totale tabella') +
              ': ' +
              r.total_for_table,
          });
          this.showPopulateDialog = false;
          this.filterTable = r.src_table;
          this.load();
        },
        error: (err) => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: this.lang.el('Errore'),
            detail: err?.error?.Message || this.lang.el('Populate fallito'),
          });
        },
      });
  }

  exportMissing(): void {
    this.loading = true;
    // Sempre tutte le righe con almeno una lingua vuota (tutte le tabelle)
    this.apiService.post('app/des_comp_i18n_export', {}).subscribe({
      next: (data) => {
        this.loading = false;
        if (data.Result === 'error') {
          this.messageService.add({
            severity: 'error',
            summary: this.lang.el('Errore'),
            detail: data.Message || this.lang.el('Esportazione fallita'),
          });
          return;
        }
        const rows = data.Result || [];
        if (!rows.length) {
          this.messageService.add({
            severity: 'info',
            summary: this.lang.el('Esporta'),
            detail: this.lang.el('Nessuna riga con descrizione vuota'),
          });
          return;
        }
        const csv = this.buildCsv(rows);
        const stamp = new Date().toISOString().slice(0, 10);
        this.downloadTextFile(csv, 'des_comp_i18n_missing_' + stamp + '.csv');
        this.messageService.add({
          severity: 'success',
          summary: this.lang.el('Esporta'),
          detail: this.lang.el('Righe esportate') + ': ' + rows.length,
        });
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.lang.el('Errore'),
          detail: err?.error?.Message || this.lang.el('Esportazione fallita'),
        });
      },
    });
  }

  openImportDialog(): void {
    this.importFileName = '';
    this.importPreviewCount = 0;
    this.importRows = [];
    this.showImportDialog = true;
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    this.importFileName = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        this.importRows = this.parseCsv(text);
        this.importPreviewCount = this.importRows.filter((r) =>
          this.langColumns.some((c) => (r[c] || '').toString().trim() !== '') ||
          (r.des_lang || '').toString().trim() !== '',
        ).length;
      } catch (e) {
        this.importRows = [];
        this.importPreviewCount = 0;
        this.messageService.add({
          severity: 'error',
          summary: this.lang.el('Errore'),
          detail: this.lang.el('File CSV non valido'),
        });
      }
    };
    reader.readAsText(file, 'UTF-8');
    input.value = '';
  }

  confirmImport(): void {
    if (!this.importRows.length) {
      this.messageService.add({
        severity: 'warn',
        summary: this.lang.el('Attenzione'),
        detail: this.lang.el('Nessuna riga da importare'),
      });
      return;
    }

    if (!this.importPreviewCount) {
      this.messageService.add({
        severity: 'warn',
        summary: this.lang.el('Attenzione'),
        detail: this.lang.el('Il CSV non contiene traduzioni compilate (colonne des_en, des_fr, ... vuote). Usa il file tradotto, non quello delle vuote.'),
        life: 15000,
      });
      return;
    }

    // sanity: prima riga deve avere almeno una lingua
    const sample = this.importRows[0];

    this.confirmationService.confirm({
      message:
        this.lang.el('Importare') +
        ' ' +
        this.importPreviewCount +
        ' ' +
        this.lang.el('traduzioni') +
        '? ' +
        this.lang.el('Se la chiave esiste le traduzioni vengono sostituite.'),
      header: this.lang.el('Conferma importazione'),
      icon: 'pi pi-upload',
      acceptLabel: this.lang.el('Sì'),
      rejectLabel: this.lang.el('No'),
      accept: () => this.doImport(),
    });
  }

  doImport(): void {
    this.loading = true;
    this.apiService
      .post('app/des_comp_i18n_import', {
        rows: this.importRows,
      })
      .subscribe({
        next: (data) => {
          this.loading = false;
          if (data.Result === 'error') {
            this.messageService.add({
              severity: 'error',
              summary: this.lang.el('Errore'),
              detail: data.Message || this.lang.el('Importazione fallita'),
            });
            return;
          }
          const r = data.Result;
          const detail =
            this.lang.el('Ricevute') +
            ': ' +
            (r.received ?? this.importRows.length) +
            ' — ' +
            this.lang.el('Righe aggiornate') +
            ': ' +
            (r.updated_rows ?? r.updated) +
            ' — ' +
            this.lang.el('Campi') +
            ': ' +
            r.updated +
            ' — ' +
            this.lang.el('Saltate') +
            ': ' +
            r.skipped +
            ' — ' +
            this.lang.el('Non trovate') +
            ': ' +
            r.not_found_or_filled;

          this.messageService.add({
            severity: (r.updated_rows || r.updated) > 0 ? 'success' : 'warn',
            summary: this.lang.el('Importa'),
            detail,
            life: 12000,
          });

          if ((r.updated_rows || r.updated) === 0) {
            console.warn('des_comp_i18n_import no updates', r);
            if (r.first_skip_keys || r.first_skip_langs) {
              console.warn('first skipped row keys', r.first_skip_keys, r.first_skip_langs);
            }
          }

          this.showImportDialog = false;
          this.load();
        },
        error: (err) => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: this.lang.el('Errore'),
            detail: err?.error?.Message || this.lang.el('Importazione fallita'),
          });
        },
      });
  }

  private buildCsv(rows: any[]): string {
    const header = ['id', 'src_table', 'des_comp_it', ...this.langColumns];
    const lines = [header.join(';')];
    for (const row of rows) {
      lines.push(
        [
          this.csvEscape(row.id),
          this.csvEscape(row.src_table),
          this.csvEscape(row.des_comp_it),
          ...this.langColumns.map((c) => this.csvEscape(row[c] || '')),
        ].join(';'),
      );
    }
    return '\uFEFF' + lines.join('\r\n');
  }

  private parseCsv(text: string): any[] {
    const cleaned = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = cleaned.split('\n').filter((l) => l.trim() !== '');
    if (lines.length < 2) {
      return [];
    }
    const delim = lines[0].includes(';') ? ';' : ',';
    const headers = this.splitCsvLine(lines[0], delim).map((h) =>
      h.trim().toLowerCase().replace(/^"|"$/g, ''),
    );
    const idx = (name: string) => headers.indexOf(name);

    const iId = idx('id');
    const iTable = idx('src_table');
    const iIt = idx('des_comp_it');
    const iLangCode = idx('lang');
    const iDesLang = idx('des_lang');

    const out: any[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = this.splitCsvLine(lines[i], delim).map((c) => c.trim());
      const item: any = {
        id: iId >= 0 ? cols[iId] : null,
        src_table: iTable >= 0 ? cols[iTable] : '',
        des_comp_it: iIt >= 0 ? cols[iIt] : '',
      };

      for (const col of this.langColumns) {
        const ci = idx(col);
        if (ci >= 0) {
          item[col] = cols[ci] || '';
        }
      }

      // legacy long format
      if (iLangCode >= 0 && iDesLang >= 0) {
        item.lang = cols[iLangCode] || '';
        item.des_lang = cols[iDesLang] || '';
      }

      out.push(item);
    }
    return out;
  }

  private splitCsvLine(line: string, delim: string): string[] {
    const result: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === delim && !inQuotes) {
        result.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    result.push(cur);
    return result;
  }

  private csvEscape(value: any): string {
    const s = value === null || value === undefined ? '' : String(value);
    if (/[;"\r\n]/.test(s)) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  }

  private downloadTextFile(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  confirmReplace(): void {
    if (!this.replaceFind) {
      this.messageService.add({
        severity: 'warn',
        summary: this.lang.el('Attenzione'),
        detail: this.lang.el('Testo da cercare obbligatorio'),
      });
      return;
    }

    this.confirmationService.confirm({
      message:
        this.lang.el('Sostituire') +
        ' "' +
        this.replaceFind +
        '" → "' +
        this.replaceWith +
        '" (' +
        this.replaceLang +
        ')' +
        (this.filterTable ? ' — ' + this.filterTable : '') +
        '?',
      header: this.lang.el('Conferma replace'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.lang.el('Sì'),
      rejectLabel: this.lang.el('No'),
      accept: () => this.doReplace(),
    });
  }

  doReplace(): void {
    this.loading = true;
    const params: any = {
      lang: this.replaceLang,
      find: this.replaceFind,
      replace: this.replaceWith,
    };
    if (this.filterTable) {
      params.src_table = this.filterTable;
    }

    this.apiService.post('app/des_comp_i18n_replace', params).subscribe({
      next: (data) => {
        this.loading = false;
        if (data.Result === 'error') {
          this.messageService.add({
            severity: 'error',
            summary: this.lang.el('Errore'),
            detail: data.Message || this.lang.el('Replace fallito'),
          });
          return;
        }
        this.messageService.add({
          severity: 'success',
          summary: this.lang.el('Replace'),
          detail: this.lang.el('Righe aggiornate') + ': ' + data.Result.affected,
        });
        this.load();
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.lang.el('Errore'),
          detail: err?.error?.Message || this.lang.el('Replace fallito'),
        });
      },
    });
  }

  saveRow(row: any): void {
    const payload: any = { id: row.id };
    for (const col of this.langColumns) {
      payload[col] = row[col] ?? '';
    }
    this.apiService.post('app/des_comp_i18n_update', payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.lang.el('Salvato'),
          detail: '#' + row.id,
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.lang.el('Errore'),
          detail: this.lang.el('Salvataggio fallito'),
        });
      },
    });
  }
}
